/**
 * Infinite scroll for `ListC`, replacing `vue3-infinite-scroll-good` (#352).
 *
 * The package it replaces has two defects that are not cosmetic:
 *
 *  1. **It never unbinds.** Its `unmounted` hook is commented out in the
 *     published source, so the `scroll` listener a list registers stays on the
 *     window after the list is destroyed. Measured on this app: navigating
 *     Centros de Custo → Transacções → Centros de Custo → Transacções leaves
 *     four listeners where there should be one, and each of them still calls
 *     the `infiniteLoadMore` of a component that no longer exists. A destroyed
 *     component's methods still work — they reach the same Vuex store the live
 *     page is rendering from — so the visible list is refetched, re-paged and
 *     re-rendered by pages the user left minutes ago. Observed directly: three
 *     `GET /people?…&_isCostCenter=true` fired while the transactions page was
 *     on screen, from a cost-centre list destroyed two navigations earlier.
 *
 *  2. **Its state is a module-level singleton.** The scroll check reads the
 *     *last mounted* element for geometry and for the disabled flag, whichever
 *     list the event belongs to. With more than one list in the app's lifetime,
 *     one list's "busy" silences another's paging, and one list's position
 *     decides whether another loads more.
 *
 * The symptom that led here was an e2e failure that read as a data problem: a
 * cost centre appearing twice in a list that held exactly one matching row in
 * the database (verified by query), and — in another run — the same row being
 * detached and re-created continuously, so that a click on it never landed.
 * Both are the same cause: something is reloading the list underneath the user.
 *
 * Same behaviour as before, same attribute contract, with the state per element
 * and the listener removed when the element goes:
 *
 *     <tbody
 *       v-infinite-scroll="loadMore"
 *       infinite-scroll-disabled="busy"
 *       infinite-scroll-distance="200"
 *     >
 */

const STATE = Symbol('infiniteScroll')

/** The nearest scrollable ancestor, or the window. */
function findScrollTarget(element) {
  let node = element
  while (node && node.nodeType === 1 && node.tagName !== 'HTML' && node.tagName !== 'BODY') {
    const overflowY = window.getComputedStyle(node).overflowY
    if (overflowY === 'scroll' || overflowY === 'auto') {
      return node
    }
    node = node.parentNode
  }
  return window
}

function scrollTopOf(target) {
  if (target === window) {
    return Math.max(window.pageYOffset || 0, document.documentElement.scrollTop)
  }
  return target.scrollTop
}

function visibleHeightOf(target) {
  if (target === window) {
    return document.documentElement.clientHeight
  }
  return target.clientHeight
}

function topOf(target) {
  if (target === window) {
    return scrollTopOf(window)
  }
  return target.getBoundingClientRect().top + scrollTopOf(window)
}

/** True when the bottom of `el` is within `distance` of the visible area. */
function isNearBottom(el, target, distance) {
  const viewportBottom = scrollTopOf(target) + visibleHeightOf(target)
  if (target === el) {
    return target.scrollHeight - viewportBottom <= distance
  }
  const elementBottom = topOf(el) - topOf(target) + el.offsetHeight + scrollTopOf(target)
  return viewportBottom + distance >= elementBottom
}

function check(state) {
  if (state.destroyed || state.disabled || typeof state.callback !== 'function') {
    return
  }
  // A list that is no longer in the document must not load anything, whatever
  // is still holding a reference to this handler. Defence in depth: `unmounted`
  // already removes the listener, and this makes a missed teardown harmless
  // rather than a phantom refetch.
  if (!state.el.isConnected) {
    return
  }
  if (isNearBottom(state.el, state.target, state.distance)) {
    state.callback()
  }
}

/** Trailing-edge throttle whose pending timer can be cancelled on teardown. */
function throttled(state, delay) {
  let lastRun = 0
  return () => {
    const now = Date.now()
    if (state.throttleTimer) {
      clearTimeout(state.throttleTimer)
      state.throttleTimer = null
    }
    const wait = lastRun ? delay - (now - lastRun) : 0
    if (wait <= 0) {
      lastRun = now
      check(state)
      return
    }
    state.throttleTimer = setTimeout(() => {
      state.throttleTimer = null
      lastRun = Date.now()
      check(state)
    }, wait)
  }
}

function numberAttribute(el, name, fallback) {
  const raw = el.getAttribute(name)
  if (raw === null) {
    return fallback
  }
  const value = Number(raw)
  return Number.isNaN(value) || value < 0 ? fallback : value
}

export const infiniteScroll = {
  mounted(el, binding) {
    const vm = binding.instance
    const state = {
      el,
      callback: binding.value,
      disabled: false,
      destroyed: false,
      distance: numberAttribute(el, 'infinite-scroll-distance', 0),
      target: findScrollTarget(el),
      listener: null,
      unwatch: null,
      throttleTimer: null,
      checkTimer: null,
    }
    el[STATE] = state

    state.listener = throttled(state, numberAttribute(el, 'infinite-scroll-throttle-delay', 200))
    state.target.addEventListener('scroll', state.listener)

    const disabledProperty = el.getAttribute('infinite-scroll-disabled')
    if (disabledProperty && vm) {
      state.disabled = Boolean(vm[disabledProperty])
      state.unwatch = vm.$watch(disabledProperty, (value) => {
        state.disabled = Boolean(value)
        // Freed up: the list may already be short enough to need another page.
        if (!state.disabled) {
          state.checkTimer = setTimeout(() => check(state), 0)
        }
      })
    }

    // First look, once the browser has laid the list out.
    state.checkTimer = setTimeout(() => check(state), 0)
  },

  updated(el, binding) {
    const state = el[STATE]
    if (state) {
      state.callback = binding.value
      state.distance = numberAttribute(el, 'infinite-scroll-distance', state.distance)
    }
  },

  unmounted(el) {
    const state = el[STATE]
    if (!state) {
      return
    }
    state.destroyed = true
    state.target.removeEventListener('scroll', state.listener)
    if (state.unwatch) {
      state.unwatch()
    }
    clearTimeout(state.throttleTimer)
    clearTimeout(state.checkTimer)
    delete el[STATE]
  },
}

export default {
  install(app) {
    app.directive('infinite-scroll', infiniteScroll)
  },
}
